import { useState } from "react";
import { IoSearchOutline } from "react-icons/io5";
import { BsArchive, BsThreeDotsVertical, BsTrash } from "react-icons/bs";
import { HiOutlineMail } from "react-icons/hi";
import { PiHeadset } from "react-icons/pi";
import { RiShareForward2Line } from "react-icons/ri";
import { FaRegEdit } from "react-icons/fa";
import {
  IoInformationCircleOutline,
  IoTimeOutline,
  IoBusinessOutline,
  IoCheckmarkCircle,
  IoCallOutline,
} from "react-icons/io5";
import { activitiesApiHooks, Activity } from "../../../api/activitiesApi";
import { useQueryClient } from "@tanstack/react-query";
import AudioPlayer from "./audioplayer";

const Inbox = () => {
  const queryClient = useQueryClient();

  // Fetch activities from API
  const {
    data: activitiesData = [],
    isLoading,
    error,
  } = activitiesApiHooks.useGetActivities([]);

  // Ensure activities is always an array
  const activities = Array.isArray(activitiesData) ? activitiesData : [];

  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(
    null
  );
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });

  // Tooltip state for hover
  const [hoveredActivity, setHoveredActivity] = useState<Activity | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipHovered, setTooltipHovered] = useState(false);
  const [closeTimeout, setCloseTimeout] = useState<NodeJS.Timeout | null>(null);

  // Fetch call recording when activity is selected
  const {
    data: callRecording,
    isLoading: isLoadingRecording,
    error: recordingError,
    refetch: refetchCallRecording,
  } = activitiesApiHooks.useGetActivityCallRecording(
    selectedActivity?.id?.toString() || ""
  );

  // Fetch activities by contact_id when an activity is selected
  const {
    data: contactActivitiesData = [],
    isLoading: isLoadingContactActivities,
    error: contactActivitiesError,
  } = activitiesApiHooks.useGetActivities(
    [],
    selectedActivity?.contactPhone?.contact?.id?.toString()
  );

  // Ensure contactActivities is always an array
  const contactActivities = Array.isArray(contactActivitiesData)
    ? contactActivitiesData
    : [];

  // Mutation to mark activity as read
  const markAsReadMutation = activitiesApiHooks.usePutActivityRead({
    onSuccess: (_data: Activity, activityId: string) => {
      // Update the main activities cache
      queryClient.setQueryData<Activity[]>(
        ["activities", [], undefined],
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((activity) =>
            activity.id.toString() === activityId
              ? { ...activity, isRead: true }
              : activity
          );
        }
      );

      // Update contact activities cache if it exists
      queryClient.setQueryData<Activity[]>(
        [
          "activities",
          [],
          selectedActivity?.contactPhone?.contact?.id?.toString(),
        ],
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((activity) =>
            activity.id.toString() === activityId
              ? { ...activity, isRead: true }
              : activity
          );
        }
      );
    },
  });

  // Handle activity selection and mark as read if needed
  const handleActivityClick = (activity: Activity) => {
    setSelectedActivity(activity);
    if (!activity.isRead) {
      markAsReadMutation.mutate(activity.id.toString());
    }
  };

  // Close dialog when clicking outside
  const handleClickOutside = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setShowDetailsDialog(false);
    }
  };

  // Handle button click to get position
  const handleDetailsButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setButtonPosition({
      top: rect.bottom + window.scrollY,
      left: rect.right - 320 + window.scrollX, // 320px is dialog width (w-80 = 320px)
    });
    setShowDetailsDialog(true);
  };

  // Handle hover events for tooltip
  const handleActivityHover = (
    activity: Activity,
    e: React.MouseEvent<HTMLDivElement>
  ) => {
    // Clear any existing timeout
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      setCloseTimeout(null);
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const tooltipWidth = 320; // w-80 = 320px

    // Calculate position - try to show to the right, but if not enough space, show to the left
    let left = rect.right + window.scrollX + 10;
    if (left + tooltipWidth > viewportWidth) {
      left = rect.left + window.scrollX - tooltipWidth - 10;
    }

    setTooltipPosition({
      top: rect.top + window.scrollY,
      left: left,
    });
    setHoveredActivity(activity);
    setShowTooltip(true);
    setTooltipHovered(false);
  };

  const handleActivityLeave = () => {
    // Start a delay before closing the tooltip
    const timeout = setTimeout(() => {
      if (!tooltipHovered) {
        setShowTooltip(false);
        setHoveredActivity(null);
      }
    }, 300); // 300ms delay
    setCloseTimeout(timeout);
  };

  const handleTooltipEnter = () => {
    // Clear any existing timeout when hovering over tooltip
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      setCloseTimeout(null);
    }
    setTooltipHovered(true);
  };

  const handleTooltipLeave = () => {
    setTooltipHovered(false);
    // Start a delay before closing the tooltip
    const timeout = setTimeout(() => {
      setShowTooltip(false);
      setHoveredActivity(null);
    }, 300); // 300ms delay
    setCloseTimeout(timeout);
  };

  // Helper function to generate avatar initials
  const getAvatarInitials = (
    firstName: string,
    lastName: string,
    phone: string
  ) => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    } else if (phone) {
      // Show last 4 digits of phone number
      return phone.slice(-4);
    }
    return "??";
  };

  // Helper function to format date for inbox list
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();

    // Reset time to start of day for accurate comparison
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const inputDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    // Format time as HH:MM
    const timeString = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    if (inputDate.getTime() === today.getTime()) {
      return `Today ${timeString}`;
    } else if (inputDate.getTime() === yesterday.getTime()) {
      return `Yesterday ${timeString}`;
    } else if (now.getTime() - date.getTime() <= 7 * 24 * 60 * 60 * 1000) {
      const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
      return `${weekday} ${timeString}`;
    }
    const formattedDate = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return `${formattedDate} ${timeString}`;
  };

  // Helper function to format date for dialog (6 Nov 2024 11:46 AM format)
  const formatDateForDialog = (dateString: string) => {
    const date = new Date(dateString);

    // Format time as HH:MM AM/PM
    const timeString = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    const formattedDate = date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    return `${formattedDate} ${timeString}`;
  };

  // Helper function to format call duration
  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // Set first activity as selected by default
  if (activities.length > 0 && !selectedActivity) {
    setSelectedActivity(activities[0]);
  }

  return (
    <div className="h-screen bg-gray-100 p-1">
      <div className="grid grid-cols-[350px_1fr_300px] gap-1 h-full">
        {/* Activities List */}
        <div className="bg-white rounded-xl flex flex-col">
          <div className="p-4 border-b">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-xl font-semibold">Activities</h1>
                <p className="text-sm text-gray-500">
                  {isLoading
                    ? "Loading..."
                    : `All activities (${activities.length})`}
                </p>
              </div>
              <button className="text-gray-400">
                <BsThreeDotsVertical size={20} />
              </button>
            </div>
            <div className="relative">
              <IoSearchOutline className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search activities"
                className="w-full pl-10 pr-4 py-2 border rounded-full text-sm"
              />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
            {isLoading ? (
              <div className="flex justify-center items-center h-32">
                <div className="text-gray-500">Loading activities...</div>
              </div>
            ) : error ? (
              <div className="flex justify-center items-center h-32">
                <div className="text-red-500">Error loading activities</div>
              </div>
            ) : (
              activities.map((activity: Activity) => (
                <div
                  key={activity.id}
                  onClick={() => handleActivityClick(activity)}
                  className={`flex items-center gap-3 p-4 cursor-pointer border-b ${
                    selectedActivity?.id === activity.id
                      ? "bg-blue-50"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#78C7D1] text-white flex items-center justify-center font-medium">
                    {getAvatarInitials(
                      activity.contactPhone?.contact?.firstName || "",
                      activity.contactPhone?.contact?.lastName || "",
                      activity.contactPhone?.phone || ""
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between">
                      <p className="font-medium truncate">
                        {activity.contactPhone?.contact?.firstName &&
                        activity.contactPhone?.contact?.lastName
                          ? `${activity.contactPhone.contact.firstName} ${activity.contactPhone.contact.lastName}`
                          : activity.contactPhone?.phone || "Unknown Contact"}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">
                          {formatDate(activity.createdAt)}
                        </span>
                        {!activity.isRead && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 truncate">
                      {activity.contactPhone?.contact?.company || "N/A"} •{" "}
                      {formatDuration(activity.durationCall)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Activity Detail */}
        <div className="bg-white rounded-xl p-6">
          {selectedActivity ? (
            <>
              {/* Top row with button */}
              <div className="flex justify-end mb-4">
                <button
                  onClick={handleDetailsButtonClick}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                  title="Show more details"
                >
                  <IoInformationCircleOutline size={20} />
                </button>
              </div>

              {/* Main content row */}
              <div className="flex justify-between items-start mb-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#78C7D1] text-white flex items-center justify-center font-medium">
                    {getAvatarInitials(
                      selectedActivity.contactPhone?.contact?.firstName || "",
                      selectedActivity.contactPhone?.contact?.lastName || "",
                      selectedActivity.contactPhone?.phone || ""
                    )}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">
                      {selectedActivity.contactPhone?.contact?.firstName &&
                      selectedActivity.contactPhone?.contact?.lastName
                        ? `${selectedActivity.contactPhone.contact.firstName} ${selectedActivity.contactPhone.contact.lastName}`
                        : selectedActivity.contactPhone?.phone ||
                          "Unknown Contact"}
                    </h2>
                    <div className="text-sm text-gray-500">
                      <span>
                        {selectedActivity.contactPhone?.contact?.company}{" "}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">
                    {formatDateForDialog(selectedActivity.createdAt)}
                  </div>
                  <div className="text-sm text-teal-500 flex items-center gap-1 justify-end">
                    <IoCallOutline size={14} />
                    {selectedActivity.contactPhone?.phone}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex justify-center items-center h-32">
              <div className="text-gray-500">
                Select an activity to view details
              </div>
            </div>
          )}

          {selectedActivity && (
            <>
              {/* Audio Player */}
              <div className="mb-6">
                <AudioPlayer
                  audioUrl={callRecording?.link || ""}
                  isLoading={isLoadingRecording}
                  error={recordingError}
                  onRefreshUrl={refetchCallRecording}
                  urlTimestamp={callRecording?.timestamp}
                />
              </div>

              <div className="space-y-6">
                {selectedActivity.formData.company && (
                  <div className="border-b border-gray-200 pb-4">
                    <div className="text-xs text-gray-500 mb-2">Company</div>
                    <div className="text-sm text-gray-900">
                      {selectedActivity.formData.company}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="flex gap-3 mt-20 justify-center">
            <button className="flex items-center gap-2 px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">
              <FaRegEdit size={16} />
              Note
            </button>
            <button className="flex items-center gap-2 px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">
              <RiShareForward2Line size={16} />
              Share
            </button>
          </div>
        </div>

        {/* Contact Activities */}
        <div className="bg-white rounded-xl flex flex-col">
          <div className="p-4 border-b">
            <h2 className="text-xl font-semibold">
              {selectedActivity ? "Recent Activities" : "Recent Activities"}
            </h2>
            {selectedActivity && (
              <p className="text-sm text-gray-500 mt-1">
                {selectedActivity.contactPhone?.contact?.firstName &&
                selectedActivity.contactPhone?.contact?.lastName
                  ? `${selectedActivity.contactPhone.contact.firstName} ${selectedActivity.contactPhone.contact.lastName}`
                  : selectedActivity.contactPhone?.phone || "Unknown Contact"}
              </p>
            )}
          </div>
          <div className="flex-1 overflow-auto">
            {isLoadingContactActivities ? (
              <div className="flex justify-center items-center h-32">
                <div className="text-gray-500">Loading activities...</div>
              </div>
            ) : contactActivitiesError ? (
              <div className="flex justify-center items-center h-32">
                <div className="text-red-500">Error loading activities</div>
              </div>
            ) : contactActivities.length > 0 ? (
              contactActivities.map((activity: Activity) => (
                <div
                  key={activity.id}
                  className={`p-4 border-b hover:bg-gray-50 cursor-pointer ${
                    selectedActivity?.id === activity.id ? "bg-blue-50" : ""
                  }`}
                  onClick={() => handleActivityClick(activity)}
                  // onMouseEnter={(e) => handleActivityHover(activity, e)}
                  onMouseLeave={handleActivityLeave}
                  onMouseMove={(e) => handleActivityHover(activity, e)}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-500">Secretary</p>
                      <p className="font-medium">
                        {activity.secretary.firstName}{" "}
                        {activity.secretary.lastName}
                      </p>
                      <p className="text-xs text-gray-400">
                        {activity.contactPhone?.contact?.firstName &&
                        activity.contactPhone?.contact?.lastName
                          ? `${activity.contactPhone.contact.firstName} ${activity.contactPhone.contact.lastName}`
                          : activity.contactPhone?.phone || "Unknown Contact"}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className="text-sm text-gray-500">
                        {formatDate(activity.createdAt)}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatDuration(activity.durationCall)}
                      </span>
                      <div className="flex items-center gap-1">
                        <HiOutlineMail className="text-gray-400" />
                        {!activity.isRead && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex justify-center items-center h-32">
                <div className="text-gray-500">
                  {selectedActivity
                    ? "No activities found for this contact"
                    : "Select an activity to view contact activities"}
                </div>
              </div>
            )}
          </div>
          <div className="p-4 mb-8">
            <p className="text-sm text-gray-500 mb-4">Actions</p>
            <div className="flex flex-col gap-3">
              <button className="flex items-center gap-2 text-gray-600 text-sm">
                <BsArchive size={16} />
                Archive
              </button>
              <button className="flex items-center gap-2 text-gray-600 text-sm">
                <BsTrash size={16} />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Details Dialog */}
      {showDetailsDialog && selectedActivity && (
        <div className="fixed inset-0 z-50" onClick={handleClickOutside}>
          <div
            className="absolute bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-80"
            style={{
              top: `${buttonPosition.top}px`,
              left: `${buttonPosition.left}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Call Details List */}
            <div className="space-y-2">
              {/* From */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">
                  From {selectedActivity.contactPhone?.phone}
                </span>
              </div>

              {/* To */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">
                  To {selectedActivity.businessLine.line?.phone}
                </span>
              </div>

              {/* Secretary */}
              <div className="flex items-center gap-2">
                <PiHeadset className="text-gray-500" size={14} />
                <span className="text-sm text-gray-700">
                  {selectedActivity.secretary.firstName}{" "}
                  {selectedActivity.secretary.lastName}
                </span>
              </div>

              {/* Date & Time */}
              <div className="flex items-center gap-2">
                <IoTimeOutline className="text-gray-500" size={14} />
                <span className="text-sm text-gray-700">
                  {formatDateForDialog(selectedActivity.createdAt)}
                </span>
              </div>

              {/* Company */}
              <div className="flex items-center gap-2">
                <IoBusinessOutline className="text-gray-500" size={14} />
                <span className="text-sm text-gray-700">
                  {selectedActivity.contactPhone?.contact?.company}
                </span>
              </div>

              {/* Email with checkmark */}
              <div className="flex items-center gap-2">
                <HiOutlineMail className="text-gray-500" size={14} />
                <span className="text-sm text-green-600 flex items-center gap-1">
                  {selectedActivity.secretary.email}
                  <IoCheckmarkCircle className="text-green-500" size={12} />
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activity Tooltip */}
      {showTooltip && hoveredActivity && (
        <div
          className="fixed z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-6 w-96 max-h-[700px] overflow-y-auto"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
          }}
          onMouseEnter={handleTooltipEnter}
          onMouseLeave={handleTooltipLeave}
        >
          <div className="space-y-4">
            {/* Header */}
            <div className="border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2">
                <IoInformationCircleOutline
                  className="text-blue-500"
                  size={20}
                />
                <h3 className="font-semibold text-gray-900">
                  Activity Details
                </h3>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                ID: {hoveredActivity.id}
              </p>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-3">
                <IoBusinessOutline className="text-green-500" size={16} />
                <h4 className="font-medium text-gray-700">Contact</h4>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Name:</span>
                  <span className="font-medium text-gray-900">
                    {hoveredActivity.contactPhone?.contact?.firstName || "N/A"}{" "}
                    {hoveredActivity.contactPhone?.contact?.lastName || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Company:</span>
                  <span className="text-gray-900">
                    {hoveredActivity.contactPhone?.contact?.company || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Phone:</span>
                  <span className="text-blue-600 font-mono">
                    {hoveredActivity.contactPhone?.phone || "N/A"}
                  </span>
                </div>
                {hoveredActivity.formData.email && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Email:</span>
                    <span className="text-gray-900">
                      {hoveredActivity.formData.email}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Secretary Information */}
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-3">
                <PiHeadset className="text-blue-500" size={16} />
                <h4 className="font-medium text-gray-700">Secretary</h4>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Name:</span>
                  <span className="font-medium text-gray-900">
                    {hoveredActivity.secretary.firstName}{" "}
                    {hoveredActivity.secretary.lastName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email:</span>
                  <span className="text-gray-900">
                    {hoveredActivity.secretary.email}
                  </span>
                </div>
              </div>
            </div>

            {/* Call Information */}
            <div className="bg-green-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-3">
                <IoCallOutline className="text-green-500" size={16} />
                <h4 className="font-medium text-gray-700">Call Details</h4>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Duration:</span>
                  <span className="font-medium text-green-700">
                    {formatDuration(hoveredActivity.durationCall)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Line:</span>
                  <span className="font-mono text-gray-900">
                    {hoveredActivity.businessLine.line?.phone}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Created:</span>
                  <span className="text-gray-900">
                    {formatDateForDialog(hoveredActivity.createdAt)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Status:</span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      hoveredActivity.isRead
                        ? "bg-green-100 text-green-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {hoveredActivity.isRead ? "Read" : "Unread"}
                  </span>
                </div>
              </div>
            </div>

            {/* Form Data */}
            {hoveredActivity.formData.company && (
              <div className="bg-purple-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-3">
                  <HiOutlineMail className="text-purple-500" size={16} />
                  <h4 className="font-medium text-gray-700">Form Data</h4>
                </div>
                <div className="space-y-2 text-sm">
                  {hoveredActivity.formData.company && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Company:</span>
                      <span className="text-gray-900">
                        {hoveredActivity.formData.company}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Inbox;
